import { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  Criterion,
  IaTargetSuggestion,
  useParticipantFieldsByName,
} from '@app/api';
import { CenteredModal, SquareButton } from '@app/core';
import { Button, ThreeDotsSpinner } from 'components';
import { TargetIaCriterionPreview } from './TargetIaCriterionPreview';
import {
  buildTargetFromIaSuggestion,
  IaTargetCriterion,
} from 'lib/iaTargetUtils';

interface Props {
  attempts: IaTargetSuggestion[];
  criteria: Criterion[];
  allowedFields?: string[];
  generating: boolean;
  isOpen: boolean;
  onClose: () => void;
  onRetry: () => void;
  onAccept: (items: IaTargetCriterion[]) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  headerIcon: {
    fontSize: theme.iconSize.regular,
    lineHeight: '100%',
    color: theme.colors.accentAccessible,
  },
  criteria: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 3,
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  criterion: {
    border: `1px solid ${theme.colors.grayBorder}`,
    borderRadius: theme.borderRadius.std,
    paddingBlock: theme.marginBase * 2,
    paddingInline: theme.marginBase * 2.5,
  },
  criterionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.marginBase * 1.5,
    marginBottom: theme.marginBase * 1.5,
  },
  label: {
    ...theme.fonts.label,
    color: theme.colors.inkPrimary,
  },
  count: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  reason: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    fontStyle: 'italic',
    textAlign: 'right',
  },
  reasonIcon: {
    fontSize: theme.iconSize.small,
    marginRight: theme.marginBase / 2,
    verticalAlign: 'middle',
  },
  empty: {
    ...theme.fonts.body,
    color: theme.colors.inkSecondary,
  },
  buttonIcon: {
    fontSize: theme.iconSize.small,
    lineHeight: '100%',
    marginRight: theme.marginBase,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.marginBase * 4,
  },
  spinner: {
    '& .dot-elastic, & .dot-elastic::before, & .dot-elastic::after': {
      backgroundColor: theme.colors.accentAccessible,
    },
  },
  attempts: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  attemptCount: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    minWidth: theme.marginBase * 5,
    textAlign: 'center',
  },
}));

export const TargetIaSuggestion = ({
  attempts,
  criteria,
  allowedFields,
  generating,
  isOpen,
  onClose,
  onRetry,
  onAccept,
}: Props) => {
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const fieldsByName = useParticipantFieldsByName();

  const [displayed, setDisplayed] = useState(0);
  const newestId = attempts[attempts.length - 1]?.id;
  useEffect(() => {
    setDisplayed(Math.max(attempts.length - 1, 0));
  }, [newestId, attempts.length]);

  const items = useMemo(
    () =>
      buildTargetFromIaSuggestion(
        attempts[displayed]?.result,
        fieldsByName,
        criteria,
        allowedFields,
      ),
    [attempts, displayed, fieldsByName, criteria, allowedFields],
  );
  const loading = generating && attempts.length === 0;

  const accept = useCallback(() => onAccept(items), [onAccept, items]);

  return (
    <CenteredModal
      xlarge
      isOpen={isOpen}
      onRequestClose={onClose}
      trackingName="Targeting IA suggestion"
      chip={
        <i className={classnames('ri-sparkling-2-line', classes.headerIcon)} />
      }
      title={t('editSession.targeting.iaSuggestion.title')}
      text={
        loading
          ? t('editSession.targeting.iaSuggestion.loading')
          : t('editSession.targeting.iaSuggestion.subtitle', {
              count: items.length,
            })
      }
      leftButtons={
        !loading && (
          <div className={classes.attempts}>
            {attempts.length < 3 && (
              <Button
                key="retry"
                className={classes.spinner}
                color="ink"
                variant="muted"
                onClick={onRetry}
                isLoading={generating}
                disabled={generating}
              >
                <i
                  className={classnames('ri-refresh-line', classes.buttonIcon)}
                />
                {t('editSession.targeting.iaSuggestion.retry')}
              </Button>
            )}
            {attempts.length > 1 && (
              <>
                <SquareButton
                  icon="ri-arrow-left-s-line"
                  color="ink"
                  disabled={displayed === 0}
                  onClick={() => setDisplayed(displayed - 1)}
                />
                <span className={classes.attemptCount}>
                  {displayed + 1} / {attempts.length}
                </span>
                <SquareButton
                  icon="ri-arrow-right-s-line"
                  color="ink"
                  disabled={displayed >= attempts.length - 1}
                  onClick={() => setDisplayed(displayed + 1)}
                />
              </>
            )}
          </div>
        )
      }
      buttons={
        !loading && (
          <Button
            key="use"
            color="accent"
            onClick={accept}
            disabled={generating || !items.length}
          >
            <i
              className={classnames(
                'ri-checkbox-circle-line',
                classes.buttonIcon,
              )}
            />
            {t('editSession.targeting.iaSuggestion.use')}
          </Button>
        )
      }
    >
      {loading ? (
        <div className={classnames(classes.loading, classes.spinner)}>
          <ThreeDotsSpinner />
        </div>
      ) : (
        <div className={classes.criteria}>
          {!items.length && (
            <span className={classes.empty}>
              {t('editSession.targeting.iaSuggestion.empty')}
            </span>
          )}
          {items.map(({ field, criterion, reason, selectedCount }) => (
            <div key={field.name} className={classes.criterion}>
              <div className={classes.criterionHeader}>
                <span className={classes.label}>
                  {field.label}
                  {selectedCount > 1 && (
                    <span className={classes.count}>
                      {' · '}
                      {t('editSession.targeting.iaSuggestion.selected', {
                        count: selectedCount,
                      })}
                    </span>
                  )}
                </span>
                {!!reason && (
                  <span className={classes.reason}>
                    <i
                      className={classnames(
                        'ri-double-quotes-l',
                        classes.reasonIcon,
                      )}
                    />
                    {reason}
                  </span>
                )}
              </div>
              <TargetIaCriterionPreview field={field} criterion={criterion} />
            </div>
          ))}
        </div>
      )}
    </CenteredModal>
  );
};
